import React from 'react';
import PropTypes from 'prop-types';
import { Avatar as AntAvatar, Dropdown, Menu } from 'antd';
import classNames from 'classnames';
import { parseFullName } from 'parse-full-name';
import Typography from '../Typography/index';
import './avatar.scss';
import COLORS from '../../util/constants/colors';
import { SignOut, GraduationCap } from '@phosphor-icons/react';
import { connect } from 'react-redux';
import { logout } from '../../actions/auth';
// Aliased so the destructured prop below doesn't shadow the imported action.
import { openTutorialMenu as openTutorialMenuAction } from '../../actions/tutorial';
import captureTutorialAnalytics from '../tutorial/tutorialAnalytics';

export const Avatar = ({
  size,
  online,
  content,
  name,
  org,
  email,
  full,
  header,
  impersonating,
  logout,
  openTutorialMenu,
  ...props
}) => {
  const AvatarInfo = () => {
    const parsedName = parseFullName(name);
    const firstName = parsedName.first || parsedName.last;

    return (
      <div className="avatar-info">
        {
          size === 'small' ? (
            <>
              { name && <Typography variant="text" size="small" bold color={COLORS.TEXT_SECONDARY_LITE}>{firstName}</Typography> }
              { org && <Typography variant="text" size="xsmall" color={COLORS.TEXT_TERTIARY_LITE}>{org}</Typography> }
            </>
          ) : (
            <>
              { name && (
              <Typography variant="text" size="large" bold>
                Hello, {firstName}
              </Typography>
              ) }
              { org && <Typography variant="text" size="medium" bold color={COLORS.TEXT_TERTIARY_LITE}>{org}</Typography> }
              { email && <Typography variant="text" size="small" color={COLORS.TEXT_TERTIARY_LITE}>{email}</Typography> }
            </>
          )
        }
      </div>
    );
  };

  const avatarSize = size === 'small' ? 32 : 48;
  const AvatarComponent = (
    <div className={classNames('avatar', { full, header })}>
      <div className="avatar-icon">
        <AntAvatar size={avatarSize} style={{ backgroundColor: COLORS.ORANGE_PRIMARY_LITE }} {...props}>
          {content}
        </AntAvatar>
        { online && (<div className={classNames('online-dot', { [size]: size })} />) }
      </div>
      <AvatarInfo />
    </div>
  );

  // Instrumented like every other way into the checklist (`walkthrough_completed`,
  // `guide_rail`, `completed_before_reload`) — this is the primary, always-available
  // entry point and was the one hole in the funnel, so PostHog showed tutorial
  // starts with no menu-open source. Required by app/javascript/src/AGENTS.md:20.
  const openLearnMedsender = () => {
    captureTutorialAnalytics(impersonating, 'tutorial_menu_opened', { source: 'avatar_menu' });
    openTutorialMenu();
  };

  if (header) {
    return (
      <Dropdown
        trigger={['click']}
        overlay={
          <Menu>
            {/* Replay entry for the guided tutorials. It remains available during
                impersonation, where the walkthrough is useful for support and
                progress writes are intentionally disabled. */}
            <Menu.Item key="learn-medsender" onClick={() => openLearnMedsender()}>
              <span className="logout-item">
                <GraduationCap size={16} weight="bold" fill={COLORS.PURPLE_PRIMARY_LITE} />
                <Typography variant="text" size="small">
                  Learn Medsender
                </Typography>
              </span>
            </Menu.Item>
            <Menu.Item key="logout" onClick={() => logout()}>
              <span className="logout-item">
                <SignOut size={16} weight="bold" fill={COLORS.PURPLE_PRIMARY_LITE}/>
                <Typography variant="text" size="small">
                  Logout
                </Typography>
              </span>
            </Menu.Item>
          </Menu>
        }
      >
        {AvatarComponent}
      </Dropdown>
    );
  }

  return AvatarComponent;
};

Avatar.defaultProps = {
  size: 'small',
  online: false,
  org: '',
  email: '',
  full: false,
  header: false,
  impersonating: false,
  logout: () => {},
  openTutorialMenu: () => {},
};

Avatar.propTypes = {
  size: PropTypes.string,
  online: PropTypes.bool,
  content: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  org: PropTypes.string,
  email: PropTypes.string,
  full: PropTypes.bool,
  header: PropTypes.bool,
  impersonating: PropTypes.bool,
  logout: PropTypes.func,
  openTutorialMenu: PropTypes.func,
};

export default connect((state) => ({
  impersonating: state.auth.impersonating,
}), {
  logout,
  openTutorialMenu: openTutorialMenuAction,
})(Avatar);
